/**
 * Internationalisation côté client.
 *
 * Les textes viennent de l'API (donc de la base), pas du bundle : ajouter le
 * néerlandais après l'événement pilote ne demandera aucun redéploiement du
 * frontend. Le dictionnaire est conservé en localStorage pour que l'interface
 * reste lisible au premier chargement hors ligne.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type Dictionnaire = Record<string, string>;

const Contexte = createContext<{
  t: (cle: string, defaut?: string) => string;
  pret: boolean;
  langue: string;
}>({
  t: (cle, defaut) => defaut ?? cle,
  pret: false,
  langue: 'fr',
});

const cleStockage = (langue: string) => `eventmatch.libelles.${langue}`;

function lireCache(langue: string): Dictionnaire {
  try {
    return JSON.parse(localStorage.getItem(cleStockage(langue)) ?? '{}') as Dictionnaire;
  } catch {
    return {};
  }
}

export function FournisseurLibelles({
  langue,
  children,
}: {
  langue: string;
  children: ReactNode;
}) {
  const [libelles, setLibelles] = useState<Dictionnaire>(() => lireCache(langue));
  const [pret, setPret] = useState(() => Object.keys(lireCache(langue)).length > 0);

  useEffect(() => {
    let annule = false;

    fetch(`/api/libelles/${langue}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((donnees: { libelles: Dictionnaire }) => {
        if (annule) return;
        setLibelles(donnees.libelles);
        setPret(true);
        try {
          localStorage.setItem(cleStockage(langue), JSON.stringify(donnees.libelles));
        } catch {
          // Stockage plein ou navigation privée : sans conséquence, on garde le
          // dictionnaire en mémoire pour la session.
        }
      })
      .catch(() => {
        // Hors ligne au premier chargement : on affiche ce qu'on a. `t()` retombe
        // sur le texte de repli passé à l'appel plutôt que sur la clé brute.
        if (!annule) setPret(true);
      });

    return () => {
      annule = true;
    };
  }, [langue]);

  const valeur = useMemo(
    () => ({
      t: (cle: string, defaut?: string) => libelles[cle] ?? defaut ?? cle,
      pret,
      langue,
    }),
    [libelles, pret, langue],
  );

  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useLibelles() {
  return useContext(Contexte);
}
